import { ROUTES } from 'constant/routes';

import { ShopItemInterface, SystemPreferencesResponse } from 'types';

/** Client config shop flags (from `config.shop`). */
export type ShopConfigFlags = {
  isCommerceAvailable?: boolean;
  isPackagesAvailable?: boolean;
  isGiftCardsAvailable?: boolean;
  isProductsAvailable?: boolean;
  isMembershipAvailable?: boolean;
};

const isClientGiftCardsEnabled = (shop?: ShopConfigFlags | null): boolean =>
  shop ? !!shop.isGiftCardsAvailable : true;

const isClientPackagesEnabled = (shop?: ShopConfigFlags | null): boolean =>
  shop ? !!shop.isPackagesAvailable : true;

const isClientProductsEnabled = (shop?: ShopConfigFlags | null): boolean =>
  shop ? !!shop.isProductsAvailable : true;

const isClientMembershipsEnabled = (shop?: ShopConfigFlags | null): boolean =>
  shop ? !!shop.isMembershipAvailable : true;

export const isClientShopEnabled = (shop?: ShopConfigFlags | null): boolean => {
  if (!shop) return true;

  return (
    isClientGiftCardsEnabled(shop) ||
    isClientPackagesEnabled(shop) ||
    isClientProductsEnabled(shop) ||
    isClientMembershipsEnabled(shop) ||
    !!shop.isCommerceAvailable
  );
};

const allowsByApiAndConfig = (
  apiAllowed: boolean,
  configAllowed: boolean,
  sysPrefs?: SystemPreferencesResponse | null,
  shop?: ShopConfigFlags | null
): boolean => {
  if (shop && !configAllowed) return false;
  if (!sysPrefs) return shop ? configAllowed : apiAllowed;

  return apiAllowed && (shop ? configAllowed : true);
};

/** Packages visible on site (browse). Requires `show_packages` only. */
export const canShowPackages = (
  sysPrefs?: SystemPreferencesResponse | null,
  shop?: ShopConfigFlags | null
): boolean =>
  allowsByApiAndConfig(
    !!sysPrefs?.show_packages,
    isClientPackagesEnabled(shop),
    sysPrefs,
    shop
  );

/** Packages can be purchased online. Requires `show_packages` + `allow_packages_checkout`. */
export const canPurchasePackages = (
  sysPrefs?: SystemPreferencesResponse | null,
  shop?: ShopConfigFlags | null
): boolean =>
  allowsByApiAndConfig(
    !!sysPrefs?.show_packages && !!sysPrefs?.allow_packages_checkout,
    isClientPackagesEnabled(shop),
    sysPrefs,
    shop
  );

export const canPurchaseMemberships = (
  sysPrefs?: SystemPreferencesResponse | null,
  shop?: ShopConfigFlags | null
): boolean =>
  allowsByApiAndConfig(
    !!sysPrefs?.show_memberships && !!sysPrefs?.allow_memberships_checkout,
    isClientMembershipsEnabled(shop),
    sysPrefs,
    shop
  );

export const canPurchaseGiftCards = (
  sysPrefs?: SystemPreferencesResponse | null,
  shop?: ShopConfigFlags | null
): boolean =>
  allowsByApiAndConfig(
    !!sysPrefs?.allow_gc_purchase,
    isClientGiftCardsEnabled(shop),
    sysPrefs,
    shop
  );

export const canPurchaseProducts = (
  sysPrefs?: SystemPreferencesResponse | null,
  shop?: ShopConfigFlags | null
): boolean =>
  allowsByApiAndConfig(
    !!sysPrefs?.allow_products_purchase,
    isClientProductsEnabled(shop),
    sysPrefs,
    shop
  );

/** Shop entry: client has shop surfaces and sys-prefs allow at least one category. */
export const canAccessShop = (
  sysPrefs?: SystemPreferencesResponse | null,
  shop?: ShopConfigFlags | null
): boolean => {
  if (!isClientShopEnabled(shop)) return false;
  if (!sysPrefs) return !!shop;

  return (
    canPurchaseGiftCards(sysPrefs, shop) ||
    canPurchaseProducts(sysPrefs, shop) ||
    canShowPackages(sysPrefs, shop) ||
    canPurchaseMemberships(sysPrefs, shop)
  );
};

/** Redirect target when a shop sub-route is disabled (matches legacy `/shop` fallback). */
export const getShopFallbackRoute = (
  sysPrefs?: SystemPreferencesResponse | null,
  shop?: ShopConfigFlags | null
): string => (canAccessShop(sysPrefs, shop) ? ROUTES.shop : ROUTES.home);

/** Where the header Shop control should navigate (single category → direct route). */
export const getShopDestination = (
  sysPrefs?: SystemPreferencesResponse | null,
  shop?: ShopConfigFlags | null
): string | null => {
  if (!isClientShopEnabled(shop)) return null;
  if (!sysPrefs) return null;

  const destinations: string[] = [];

  if (canPurchaseGiftCards(sysPrefs, shop)) {
    destinations.push(ROUTES.giftCardsShop);
  }
  if (canShowPackages(sysPrefs, shop)) {
    destinations.push(ROUTES.packagesShop);
  }
  if (canPurchaseProducts(sysPrefs, shop)) {
    destinations.push(ROUTES.productsShop);
  }
  if (canPurchaseMemberships(sysPrefs, shop)) {
    destinations.push(ROUTES.membershipShop);
  }

  if (destinations.length === 0) return null;
  if (destinations.length === 1) return destinations[0];

  return ROUTES.shop;
};

const matchesRoute = (link: string, route: string): boolean =>
  link === route || link.startsWith(`${route}/`);

/** Whether a configured shop landing tile is allowed by client config + sys-prefs. */
export const isShopItemEnabled = (
  item: Pick<ShopItemInterface, 'linkUrl'>,
  sysPrefs?: SystemPreferencesResponse | null,
  shop?: ShopConfigFlags | null
): boolean => {
  if (!isClientShopEnabled(shop)) return false;

  const link = item.linkUrl ?? '';

  if (!sysPrefs) {
    if (matchesRoute(link, ROUTES.giftCardsShop)) {
      return isClientGiftCardsEnabled(shop);
    }
    if (matchesRoute(link, ROUTES.packagesShop)) {
      return isClientPackagesEnabled(shop);
    }
    if (matchesRoute(link, ROUTES.membershipShop)) {
      return isClientMembershipsEnabled(shop);
    }
    if (
      matchesRoute(link, ROUTES.productsShop) ||
      matchesRoute(link, ROUTES.shopCategory) ||
      matchesRoute(link, ROUTES.shopBrand) ||
      matchesRoute(link, ROUTES.shopProduct) ||
      matchesRoute(link, ROUTES.cart)
    ) {
      return isClientProductsEnabled(shop);
    }

    return !link ? isClientPackagesEnabled(shop) : false;
  }

  if (matchesRoute(link, ROUTES.giftCardsShop)) {
    return canPurchaseGiftCards(sysPrefs, shop);
  }
  if (matchesRoute(link, ROUTES.packagesShop)) {
    return canShowPackages(sysPrefs, shop);
  }
  if (matchesRoute(link, ROUTES.membershipShop)) {
    return canPurchaseMemberships(sysPrefs, shop);
  }
  if (
    matchesRoute(link, ROUTES.productsShop) ||
    matchesRoute(link, ROUTES.shopCategory) ||
    matchesRoute(link, ROUTES.shopBrand) ||
    matchesRoute(link, ROUTES.shopProduct) ||
    matchesRoute(link, ROUTES.cart)
  ) {
    return canPurchaseProducts(sysPrefs, shop);
  }

  if (!link) {
    return canShowPackages(sysPrefs, shop);
  }

  return false;
};

export const filterShopItems = <T extends Pick<ShopItemInterface, 'linkUrl'>>(
  items: T[],
  sysPrefs?: SystemPreferencesResponse | null,
  shop?: ShopConfigFlags | null
): T[] => items.filter((item) => isShopItemEnabled(item, sysPrefs, shop));

/** Header Shop: client shop on; after prefs load, require a real navigation target. */
export const shouldShowHeaderShop = (
  sysPrefs?: SystemPreferencesResponse | null,
  shopItems: Pick<ShopItemInterface, 'linkUrl'>[] = [],
  shop?: ShopConfigFlags | null
): boolean => {
  if (!isClientShopEnabled(shop)) return false;
  if (!sysPrefs) return true;

  const dest = getShopDestination(sysPrefs, shop);
  if (!dest) return false;

  if (dest !== ROUTES.shop) return true;

  return filterShopItems(shopItems, sysPrefs, shop).length > 0;
};
